import { createContext, ReactNode, useContext, useMemo, useState } from 'react';

import useAppSelector from '~hooks/useAppSelector';
import { selectConfigBackendUrl } from '~store/selectors';

export type UiComponentCssContextType = {
  cssString?: string;
  cssFile?: string;
};

const UiComponentCssContext = createContext<UiComponentCssContextType | undefined>(undefined);
UiComponentCssContext.displayName = 'UiComponentCssContext';
const UiComponentCssSetContext = createContext<(_action: UiComponentCssContextType) => void | undefined>(undefined);
UiComponentCssSetContext.displayName = 'SetUiComponentCssContext';

const UiComponentCssProvider = ({ children }: { children: ReactNode }) => {
  const [cssString, setCssString] = useState<string>('');
  const [cssFile, setCssFile] = useState<string>(null);

  const backendUrl = useAppSelector(selectConfigBackendUrl);

  const setCssContext = ({ cssString, cssFile }: UiComponentCssContextType) => {
    setTimeout(() => {
      if (cssString) {
        setCssString(cssString);
      }
      if (cssFile) {
        // Prefix fileUrl only if relative url
        if (!cssFile.startsWith('http')) {
          cssFile = `${backendUrl}${cssFile}`;
        }
        setCssFile(cssFile);
      }
    }, 10);
  };

  const value = useMemo(
    () => ({
      cssString,
      cssFile,
    }),
    [cssFile, cssString],
  );

  return (
    <UiComponentCssContext.Provider value={value}>
      <UiComponentCssSetContext.Provider value={setCssContext}>{children}</UiComponentCssSetContext.Provider>
    </UiComponentCssContext.Provider>
  );
};

function useUiComponentCss() {
  const context = useContext(UiComponentCssContext);
  if (context === undefined) {
    throw new Error('useUiComponentCss must be used within a CssProvider');
  }
  return context;
}

function useSetUiComponentCss() {
  const context = useContext(UiComponentCssSetContext);
  if (context === undefined) {
    throw new Error('useSetUiComponentCss must be used within a CssProvider');
  }
  return context;
}

export {
  UiComponentCssContext,
  UiComponentCssProvider,
  UiComponentCssSetContext,
  useSetUiComponentCss,
  useUiComponentCss,
};
